import React from 'react'
import { cn } from '@/utils'

export type CheckboxStyle = 'style1' | 'style2' | 'style3' | 'style4' | 'style5'

type Props = {
  id: string
  name: string
  checked?: boolean
  onChange?: (checked: boolean) => void
  style?: CheckboxStyle
  className?: string
  disabled?: boolean
  children?: React.ReactNode
}

const CheckIcons = {
  style1: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className="absolute inset-0 w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="6"
        y="6"
        width="12"
        height="12"
        rx="2"
        className="fill-emerald-500"
      />
    </svg>
  ),
  style2: (
    <svg
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-3.5 h-3.5"
    >
      <path
        d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
        stroke="#002432"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  style3: (
    <svg
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-3.5 h-3.5"
    >
      <path
        d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
        stroke="#002432"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  style4: (
    <svg
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-3 h-3"
    >
      <circle cx="6" cy="6" r="5" fill="#10B981" />
    </svg>
  ),
  style5: (
    <svg
      viewBox="0 0 10 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-2.5 h-2.5"
    >
      <circle cx="5" cy="5" r="5" fill="#002432" />
    </svg>
  ),
}

export const Checkbox = ({
  id,
  name,
  checked = false,
  onChange,
  style = 'style1',
  className,
  disabled = false,
  children,
}: Props) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled && onChange) {
      onChange(e.target.checked)
    }
  }

  const getCheckboxStyle = () => {
    const baseStyles = {
      style1:
        'w-5 h-5 rounded-[5px] border border-gray-300 hover:border-emerald-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none',
      style2:
        'w-5 h-5 rounded-[5px] border border-gray-300 hover:border-emerald-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none',
      style3:
        'w-5 h-5 rounded-[5px] border border-gray-300 hover:border-emerald-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none',
      style4:
        'w-5 h-5 border border-gray-300 rounded-full hover:border-emerald-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none',
      style5:
        'w-5 h-5 border-2 border-emerald-400 rounded-full hover:border-emerald-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none',
    }

    const checkedStyles = {
      style1: 'bg-white border-emerald-500 hover:border-emerald-600',
      style2:
        'bg-emerald-500 border-emerald-500 hover:bg-emerald-600 hover:border-emerald-600',
      style3: 'border-emerald-500 hover:border-emerald-600',
      style4: 'border-emerald-500 hover:border-emerald-600',
      style5: 'border-emerald-500 hover:border-emerald-600 bg-emerald-500',
    }

    return cn(
      baseStyles[style],
      checked && checkedStyles[style],
      !disabled && 'cursor-pointer',
      disabled && 'opacity-50 cursor-not-allowed',
      'transition-all duration-200',
    )
  }

  return (
    <label
      className={cn('flex items-center gap-2', className)}
      onClick={e => e.stopPropagation()}
    >
      <div className="relative flex items-center justify-center">
        <input
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          className={cn('sr-only peer')}
          id={id}
          name={name}
        />
        <div
          className={cn(
            'relative flex items-center justify-center',
            getCheckboxStyle(),
          )}
        >
          {checked && CheckIcons[style]}
        </div>
      </div>
      {children && (
        <div
          className={cn(
            'text-gray-700 select-none text-sm',
            disabled && 'opacity-50',
          )}
        >
          {children}
        </div>
      )}
    </label>
  )
}